import { SidePanelClose, SidePanelOpen } from '@carbon/icons-react';
import { useState } from 'react';
import { CdIconButton } from '../../atoms/CdIconButton';
import { CdLogo } from '../../atoms/CdLogo';
import { CdText } from '../../atoms/CdTypography';
import { CdNavGroup, CdNavItem } from '../../molecules/CdNavItem';
import type { LinkComponent, NavItemConfig } from '../../types';
import { cx } from '../../utils';
import styles from './CdSideNav.module.scss';

export type CdSideNavMode = 'expanded' | 'rail';

export interface CdSideNavProps {
  items: NavItemConfig[];
  /** Returns true when `href` is the current location. */
  isActive: (href: string) => boolean;
  mode?: CdSideNavMode;
  /** Called by the collapse/expand toggle. Omit to hide the toggle (e.g. inside the mobile drawer). */
  onToggleMode?: () => void;
  linkComponent?: LinkComponent;
  onNavigate?: () => void;
  footerText?: string;
  label?: string;
  /** Hide the logo row (the mobile drawer renders its own header). */
  hideBrand?: boolean;
  className?: string;
}

const firstHref = (item: NavItemConfig): string | undefined =>
  item.href ?? item.children?.map(firstHref).find(Boolean);

const containsActive = (item: NavItemConfig, isActive: (href: string) => boolean): boolean =>
  (item.href ? isActive(item.href) : false) ||
  (item.children?.some((c) => containsActive(c, isActive)) ?? false);

/** Navy application navigation: expanded (16rem, nested groups) or rail (3rem, icons only). */
export const CdSideNav = ({
  items,
  isActive,
  mode = 'expanded',
  onToggleMode,
  linkComponent,
  onNavigate,
  footerText,
  label = 'Main navigation',
  hideBrand = false,
  className,
}: CdSideNavProps) => {
  // Groups containing the current page start expanded.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const open = new Set<string>();
    const visit = (list: NavItemConfig[]) =>
      list.forEach((item) => {
        if (item.children && containsActive(item, isActive)) {
          open.add(item.id);
          visit(item.children);
        }
      });
    visit(items);
    return open;
  });

  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const isRail = mode === 'rail';

  const renderItems = (list: NavItemConfig[], depth: 0 | 1 | 2) =>
    list.map((item) => {
      if (item.children && !isRail) {
        return (
          <li key={item.id}>
            <CdNavGroup
              label={item.label}
              icon={depth === 0 ? item.icon : undefined}
              depth={depth === 0 ? 0 : 1}
              expanded={openGroups.has(item.id)}
              containsActive={containsActive(item, isActive)}
              onToggle={() => toggleGroup(item.id)}
            >
              {renderItems(item.children, depth === 0 ? 1 : 2)}
            </CdNavGroup>
          </li>
        );
      }
      const href = firstHref(item) ?? '#';
      return (
        <li key={item.id}>
          <CdNavItem
            label={item.label}
            href={href}
            icon={depth === 0 ? item.icon : undefined}
            depth={depth}
            mode={mode}
            active={isRail ? containsActive(item, isActive) : isActive(href)}
            linkComponent={linkComponent}
            onNavigate={onNavigate}
          />
        </li>
      );
    });

  return (
    <nav className={cx(styles.nav, isRail && styles.rail, className)} aria-label={label}>
      {(!hideBrand || onToggleMode) && (
        <div className={styles.brand}>
          {!hideBrand && !isRail && <CdLogo tone="onDark" />}
          {onToggleMode && (
            <CdIconButton
              label={isRail ? 'Expand navigation' : 'Collapse navigation'}
              icon={isRail ? SidePanelOpen : SidePanelClose}
              onClick={onToggleMode}
              onDark
              size="md"
              align="right"
            />
          )}
        </div>
      )}
      <ul className={styles.list}>{renderItems(items, 0)}</ul>
      {footerText && !isRail && (
        <CdText variant="small" tone="onDarkMuted" className={styles.footer}>
          {footerText}
        </CdText>
      )}
    </nav>
  );
};
